/**
 * Spoken turn instructions via the Web Speech API, with music ducking.
 *
 * Language safety (the "some voices are in Thai when English is selected" bug):
 * there were two causes and both are fixed here.
 *
 *  1. `utterance.lang` was never set. With it unset the platform infers the
 *     language from the TEXT, so a sentence containing Thai characters gets a
 *     Thai voice no matter which voice object we attached.
 *  2. The text itself was Mapbox's, which splices Thai road names into English
 *     sentences. That is now handled upstream in nav/phrasing.ts, but this
 *     module still refuses to speak anything carrying the wrong script - a
 *     silent instruction is better than an unintelligible one.
 *
 * Ducking: when Spotify is playing through THIS app (Web Playback SDK), we
 * lower its volume during the announcement and restore it after.
 *
 * Sound modes (`settings.soundMode`):
 *   'voice'  - everything spoken
 *   'alerts' - no speech, a loud chime when something actually needs doing
 *   'muted'  - silence
 */

import { store } from '../core/store';
import { getLang } from '../core/i18n';
import { hasThai } from '../core/lang';

export interface Ducker {
  /** `deep` all but silences the music rather than lowering it. */
  duck(deep?: boolean): Promise<void>;
  restore(): Promise<void>;
}

/** Above this the driver has asked for more than the platform can give. */
export const BOOST_FROM = 1.01;

let ducker: Ducker | null = null;
export function setDucker(d: Ducker | null): void {
  ducker = d;
}

let voice: SpeechSynthesisVoice | null = null;
let voiceLang = 'en-US';
let voicesLoaded = false;

/** BCP-47 tag we ask the platform for. */
function tagFor(lang: 'en' | 'th'): string {
  return lang === 'th' ? 'th-TH' : 'en-US';
}

function pickVoice(): void {
  const all = speechSynthesis.getVoices();
  if (!all.length) return;
  voicesLoaded = true;

  const lang = getLang();
  const wanted = lang === 'th' ? 'th' : 'en';
  voiceLang = tagFor(lang);

  // Only ever consider voices for the ACTIVE language. If the device has none
  // (no Thai TTS installed, say), leave `voice` null and let the platform pick
  // using utterance.lang rather than falling back to a wrong-language voice.
  const match = all.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith(wanted));
  voice =
    match.find((v) => /natural|neural|premium|enhanced/i.test(v.name)) ??
    match.find((v) => v.localService) ??
    match[0] ??
    null;

  if (voice) voiceLang = voice.lang.replace('_', '-');
}

/** Re-pick after a language switch. */
export function refreshVoice(): void {
  pickVoice();
}

if ('speechSynthesis' in window) {
  pickVoice();
  speechSynthesis.addEventListener('voiceschanged', pickVoice);
}

let speaking = false;
const queue: string[] = [];
let watchdog: number | null = null;

/**
 * Release the speaking lock.
 *
 * This exists because `speaking` used to be cleared ONLY by `onend`/`onerror`,
 * and on Android neither reliably fires - the platform drops utterances when
 * the screen sleeps, when audio focus is lost to a call, or seemingly at random
 * on a long drive. One dropped utterance left `speaking` stuck true and every
 * announcement afterwards was silently swallowed for the rest of the trip.
 * That is the "sometimes the sound is just gone" report, and turning the volume
 * up would never have fixed it.
 */
function releaseLock(): void {
  speaking = false;
  if (watchdog !== null) {
    clearTimeout(watchdog);
    watchdog = null;
  }
}

/** Chrome/Android stops synthesising after ~15s unless poked. */
function keepAlive(): void {
  if (!speaking) return;
  try {
    speechSynthesis.pause();
    speechSynthesis.resume();
  } catch {
    /* not supported here */
  }
}

function speakNext(): void {
  const text = queue.shift();
  if (text === undefined) {
    releaseLock();
    return;
  }

  const s = store.get().settings;
  const u = new SpeechSynthesisUtterance(text);
  if (!voicesLoaded) pickVoice();
  if (voice) u.voice = voice;
  // Set explicitly even when a voice is attached: some Android builds ignore
  // `voice` but honour `lang`, and with neither set they sniff the text.
  u.lang = voiceLang;
  /*
   * Hard-capped at 1.0, and not by choice.
   *
   * The Web Speech API defines `volume` over 0..1 and clamps anything above
   * it; the spoken output is not routed through WebAudio, so there is no gain
   * node to insert either. A request for "louder than maximum" therefore has
   * to be answered somewhere else - see the deep duck below, and the chime
   * gain in chime().
   */
  u.volume = Math.max(0, Math.min(1, s.voiceVolume));
  u.rate = 1.04;

  speaking = true;
  let settled = false;
  const finish = async () => {
    if (settled) return;
    settled = true;
    releaseLock();
    if (queue.length) {
      speakNext();
    } else if (s.duckMusic) {
      await ducker?.restore().catch(() => {});
    }
  };
  u.onend = finish;
  u.onerror = finish;

  // Belt and braces: if the platform never calls back, give up on this
  // utterance and let the next one through. Budgeted from the length of the
  // sentence plus slack, so it never cuts a real announcement short.
  const budget = Math.max(5000, text.split(/\s+/).length * 500 + 3000);
  if (watchdog !== null) clearTimeout(watchdog);
  watchdog = window.setTimeout(() => {
    try {
      speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
    void finish();
  }, budget);
  window.setTimeout(keepAlive, 9000);

  const start = () => speechSynthesis.speak(u);
  if (s.duckMusic && ducker) {
    // Past 100% the music is pushed almost to silence instead of merely turned
    // down. Speech cannot get louder, so the gap between speech and everything
    // else is widened from the other side - which is what "louder" means in a
    // cab anyway.
    ducker
      .duck(s.voiceVolume > BOOST_FROM)
      .catch(() => {})
      .finally(start);
  } else {
    start();
  }
}

/**
 * A short, loud two-tone chime.
 *
 * Synthesised rather than shipped as a file, so it works offline and costs
 * nothing to cache. Two descending notes: a single beep reads as a
 * notification, two read as "look up". Louder than the speech, because in
 * 'alerts' mode it carries the whole message on its own.
 */
let audioCtx: AudioContext | null = null;

function audio(): AudioContext | null {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = audioCtx ?? new Ctor();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    return audioCtx;
  } catch {
    return null;
  }
}

export function chime(volume = 1): void {
  const ctx = audio();
  if (!ctx) return;
  const now = ctx.currentTime;
  const level = Math.max(0, Math.min(1.5, volume));

  /*
   * A compressor between the tones and the output, so the boost range is
   * actually usable.
   *
   * Two oscillators summed at a gain above 1 will clip, and a clipped square
   * edge is harsh rather than loud - it sounds broken and is genuinely
   * unpleasant in a confined cab. The compressor holds the peaks and lets the
   * makeup gain raise perceived loudness well past what the raw amplitude
   * could, which is the whole point of offering more than 100%.
   */
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -18;
  comp.knee.value = 12;
  comp.ratio.value = 12;
  comp.attack.value = 0.002;
  comp.release.value = 0.15;

  const master = ctx.createGain();
  master.gain.value = level;
  comp.connect(master).connect(ctx.destination);

  [988, 740].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const t0 = now + i * 0.16;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.9, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
    osc.connect(gain).connect(comp);
    osc.start(t0);
    osc.stop(t0 + 0.34);
  });
}

/**
 * Queue an announcement. Coalesces: a newer instruction replaces queued ones.
 * Text carrying a script the active language can't read is dropped - upstream
 * should have substituted a road number.
 *
 * `priority` decides what happens in 'alerts' mode: 'action' announcements
 * (turns, warnings, arrival) become a chime; 'info' ones stay silent, which is
 * the point of that mode - a quiet cabin until something needs doing.
 */
export function announce(text: string, priority: 'action' | 'info' = 'info'): void {
  const s = store.get().settings;
  if (s.soundMode === 'muted') return;

  if (s.soundMode === 'alerts') {
    if (priority === 'action') chime(s.voiceVolume);
    return;
  }

  if (!('speechSynthesis' in window)) {
    if (priority === 'action') chime(s.voiceVolume);
    return;
  }

  const trimmed = text.trim();
  if (!trimmed) return;
  if (getLang() !== 'th' && hasThai(trimmed)) {
    console.warn('[voice] refusing to speak Thai text while language is English:', trimmed);
    return;
  }

  // A queue that never drains because the lock stuck is exactly the failure
  // above; if nothing is actually being synthesised, the lock is a lie.
  if (speaking && !speechSynthesis.speaking && !speechSynthesis.pending) releaseLock();

  queue.length = 0; // stale queued instructions are worse than silence
  queue.push(trimmed);
  if (!speaking) speakNext();
}

export function stopSpeaking(): void {
  queue.length = 0;
  if ('speechSynthesis' in window) speechSynthesis.cancel();
  releaseLock();
}

/** iOS/Android unlock: call once from a user gesture so later speech works. */
export function primeSpeech(): void {
  audio(); // unlock WebAudio in the same gesture, or the first chime is lost
  if (!('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(' ');
  u.volume = 0;
  u.lang = voiceLang;
  speechSynthesis.speak(u);
}
